/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 테이블 블록에서 쓰이는 셀 블록을 정의한다.
 *
 * atomic 블록 + Text 엘리먼트 하나라는 점에서 `Images`의 `Item`과 같은 모양이다. 다른 점은 이
 * 블록 자신이 `(row, col, rowSpan, colSpan)`을 실제 상태로 들고, 그걸 그대로 자기 자신의
 * `grid-row`/`grid-column`으로 반영한다는 것 — Table 의 Container(`display:grid`)가 DOM 순서가
 * 아니라 이 좌표로 시각적 위치를 결정하므로, 열 중간 삽입·병합이 일어나도(=DOM 순서가 시각 순서와
 * 달라져도) 배치가 깨지지 않는다(Editor.v3.Table.md 참고).
 *
 * @file /src/rootin/blocks/table/blocks/Cell.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 20.
 */
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Text } from '@rootin/elements/text/Text';
import { Language, LanguageText } from '@utils/Language';
import './Cell.css';

export interface Properties {
    /**
     * 시각적 좌상단 행 좌표 (0-based, 기본값 0)
     */
    row?: number;

    /**
     * 시각적 좌상단 열 좌표 (0-based, 기본값 0)
     */
    col?: number;

    /**
     * 병합으로 차지하는 행 갯수 (기본값 1)
     */
    rowSpan?: number;

    /**
     * 병합으로 차지하는 열 갯수 (기본값 1)
     */
    colSpan?: number;
}

export class Cell extends Block {
    /**
     * 블록타입
     */
    static type = 'table-cell';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'atomic';

    /**
     * 블록아이콘
     */
    static icon: string = '';

    /**
     * 블록명
     */
    static label: LanguageText = '';

    /**
     * 셀의 텍스트 엘리먼트
     */
    text: Text;

    /**
     * 시각적 좌상단 행 좌표 (0-based)
     */
    row: number;

    /**
     * 시각적 좌상단 열 좌표 (0-based)
     */
    col: number;

    /**
     * 병합으로 차지하는 행 갯수
     */
    rowSpan: number;

    /**
     * 병합으로 차지하는 열 갯수
     */
    colSpan: number;

    /**
     * 테이블 블록에서 쓰이는 셀 블록을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     * @param properties - 좌표/병합 설정
     */
    constructor(editor: Editor, properties: Properties = null) {
        super(editor);

        this.row = properties?.row ?? 0;
        this.col = properties?.col ?? 0;
        this.rowSpan = properties?.rowSpan ?? 1;
        this.colSpan = properties?.colSpan ?? 1;

        this.text = new Text(this, { name: 'cell', role: 'table-cell-text' });
        this.$getBlock().append(this.text.$getElement());
    }

    /**
     * 현재 좌표/병합 상태를 실제 grid 배치로 반영한다. 좌표가 바뀌는 모든 경로(생성 시, 행/열
     * 삽입으로 밀려날 때, 병합/병합해제로 span이 바뀔 때)가 전부 이 메서드 하나로 모인다 —
     * `render()`뿐 아니라 그 경로들도 좌표를 바꾼 다음 이 메서드를 다시 불러야 한다(아직 그
     * 경로들 자체가 스텁이라 지금은 `render()`만 호출).
     */
    syncPosition(): void {
        const $block = this.$getBlock();
        $block.setAttr('data-row', String(this.row));
        $block.setAttr('data-col', String(this.col));
        $block.setStyle('grid-row', `${this.row + 2} / span ${this.rowSpan}`);
        $block.setStyle('grid-column', `${this.col + 2} / span ${this.colSpan}`);
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        this.syncPosition();
        await this.text.render();
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return this.text.isEmpty();
    }

    /**
     * 블록값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        if (element !== null) {
            return this.defaultValue?.[element.name] ?? null;
        }

        const value: { [key: string]: any } = {
            id: this.getId(),
            type: this.getType(),
            row: this.row,
            col: this.col,
            text: this.text.getValue(),
        };
        if (this.rowSpan > 1) {
            value.rowSpan = this.rowSpan;
        }
        if (this.colSpan > 1) {
            value.colSpan = this.colSpan;
        }

        return value;
    }

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {
        if (is_default_value === true) {
            this.defaultValue = value;
        }

        this.row = value.row;
        this.col = value.col;
        this.rowSpan = value.rowSpan ?? 1;
        this.colSpan = value.colSpan ?? 1;
        this.syncPosition();

        await this.text.setValue(value.text);
    }

    /**
     * 블록이 삽입되었을 때 이벤트를 처리한다.
     */
    async onInserted(): Promise<void> {
        this.text.focus(true);
    }

    /**
     * 블록이 포커스를 받았을 때 이벤트를 처리한다.
     *
     * @param element - 캐럿이 위치한 엘리먼트 (NULL 이면 아직 캐럿 위치가 없다는 뜻)
     */
    onFocus(element: Element | null): void {
        if (element === null) {
            this.text.focus(true);
        }
    }
}
